import type { FormProps } from 'antd';
import {
  Button,
  Form,
  Input,
  Spin,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import DealershipChooser from 'components/DealershipChooser';
import _ from 'lodash';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { AppStore, ShopStore } from 'stores';
import { Logger } from 'stores/Classes';
import styled from 'styled-components';

const { Text } = Typography;

const Container = styled.div`
  padding-right: 2vw;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 25px;
`;

type FleetSettingsFormProps = FormProps;

const FleetSettingsForm: React.FC<FleetSettingsFormProps> = () => {
  const [form] = Form.useForm();
  const [shopId, setShopId] = useState<number>(0);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);
  const [savingForm, setSavingForm] = useState<boolean>(false);
  const [fleetAddress, setFleetAddress] = useState<string>('');
  const [fleetPhone, setFleetPhone] = useState<string>('');
  const [fleetEmail, setFleetEmail] = useState<string>('');

  // Initial load of fleet settings
  useEffect(() => {
    const initialize = async () => {
      await loadData(ShopStore.currentShop.id);
      setShopId(ShopStore.currentShop.id);
    };

    initialize();
  }, []);

  const loadData = async (shopId: number) => {
    setLoadingInitial(true);
    try {
      const shop = await ShopStore.getShopById(shopId);
      // Load contact information from currentShop
      const address = _.get(shop, 'address', '');
      const phone = _.get(shop, 'phone', '');
      const email = _.get(shop, 'email', '');

      // Set contact information state
      setFleetAddress(address);
      setFleetPhone(phone);
      setFleetEmail(email);
    } catch (e) {
      Logger.log(e);
    } finally {
      setLoadingInitial(false);
    }
  };

  // Add this new useEffect to update form values after render
  useEffect(() => {
    if (!loadingInitial && form) {
      form.setFieldsValue({
        fleetAddress,
        fleetPhone,
        fleetEmail,
      });
    }
  }, [loadingInitial]); // Only watch for changes in loading state

  const updateFleetSettings = async (values: any) => {
    try {
      setSavingForm(true);
      AppStore.addLoading('Setting Fleet Settings');

      // Extract contact information from form values
      const { fleetAddress, fleetPhone, fleetEmail } = values;

      // Update shop entity with contact information
      await ShopStore.updateShop(shopId, {
        address: fleetAddress,
        phone: fleetPhone,
        email: fleetEmail,
      });

      notification.success({
        message: 'Settings updated',
        key: 'fleetSettingsSuccess',
        description: <Container>The Fleet settings were updated!</Container>,
      });
    } catch (e) {
      notification.error({
        message: 'Oops!',
        key: 'fleetSettingsError',
        description: (
          <Container>
            <Text>Error updating the fleet settings</Text>
          </Container>
        ),
      });
      Logger.log(e);
    } finally {
      AppStore.removeLoading('');
      setSavingForm(false);
    }
  };

  if (loadingInitial) {
    return (
      <LoadingContainer>
        <Spin />
      </LoadingContainer>
    );
  }

  return (
    <>
      <DealershipChooser />

      <Form onFinish={updateFleetSettings} className="edit-fleet-settings-form">
        <Form.Item
          label="Fleet Address"
          name="fleetAddress"
          initialValue={fleetAddress}
        >
          <Input placeholder="Fleet Address" />
        </Form.Item>

        <Form.Item
          label="Fleet Phone"
          name="fleetPhone"
          initialValue={fleetPhone}
        >
          <Input placeholder="Fleet Phone" />
        </Form.Item>

        <Form.Item
          label="Fleet Email"
          name="fleetEmail"
          initialValue={fleetEmail}
          rules={[{ type: 'email', message: 'Please enter a valid email' }]}
        >
          <Input placeholder="Fleet Email" />
        </Form.Item>

        <Form.Item label="AI Scanner Email" name="aiScannerEmail">
          <Text
            copyable={{
              text: `scan+${ShopStore.currentShop.id}@pitstopconnect.com`,
            }}
            strong
          >
            <Tooltip
              title="Send your work orders to this email to have them uploaded to the Bulk Work Orders page!"
              styles={{ body: { textAlign: 'center' } }}
            >
              <span>{`scan+${ShopStore.currentShop.id}@pitstopconnect.com`}</span>
            </Tooltip>
          </Text>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            className="login-form-button"
            loading={savingForm}
          >
            Update Settings
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default observer(FleetSettingsForm);
